import { ChartOptionsContextProvider } from "@/chartOptions/ChartOptionsContext";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AllAssetsContextProvider } from "@/contexts/AllAssetsContext";
import { InitialLoadContextProvider } from "@/contexts/InitialLoadContext";
import { SelectedAssetsContextProvider } from "@/contexts/SelectedAssetsContext";
import { DateRangeContextProvider } from "@/dateRanges/DateRangeContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import ReactGA from "react-ga4";
import "./index.css";
import {
  InstrumentedErrorBoundaryGenericDisplay
} from "@/components/error-boundary";
import { routeTree } from "@/routeTree.gen";
import { createRouter, Link, RouterProvider } from "@tanstack/react-router";
import { Spinner } from "@/components/ui/spinner";
import { GenericAlert } from "@/components/generic-alert";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { Toaster } from "sonner";
import { PostHogProvider } from "posthog-js/react";
import { AllauthProvider } from "@knowsuchagency/django-allauth";
import { API_URL } from "@/api/client";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    queryClient,
    auth: undefined!,
    customNav: undefined!,
    removeContentPadding: undefined!,
    hideFeedbackFooter: undefined!
  },
  scrollToTopSelectors: ["#main-content", "#main-install-flow-step-content"],
  defaultPreloadStaleTime: 0,
  defaultPreloadDelay: 50,
  // Retain installSensorKitSerialNumber across all navigations
  defaultSearchParams: (prev) => ({
    installSensorKitSerialNumber: prev.installSensorKitSerialNumber,
  }),
  notFoundMode: 'fuzzy',
  defaultNotFoundComponent: () => (
    <div className="flex h-screen w-full flex-col items-center justify-center">
      <GenericAlert>
        <p className="text-xl mb-4">
          Page not found!
        </p>
        <Button>
          <Link to="/organization" className="flex flex-row gap-2 items-center">
            Go home
            <ChevronRight/>
          </Link>
        </Button>
      </GenericAlert>
    </div>
  ),
//   we're not providing a defaultErrorComponent because errors will be caught by the higher-level ErrorBoundary
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }

  interface StaticDataRouteOption {
    breadcrumb?: () => JSX.Element
  }
}

const getTrackingId = (tool: string) => {
  const host = window.location.hostname;

  if (host === "qa.copilot.thalolabs.com") {
    if (tool === "hotjar") return 3841078;
    else if (tool === "ga") return "G-30EMBKCLJW";
  } else if (host === "copilot.thalolabs.com") {
    if (tool === "hotjar") return 3840753;
    else if (tool === "ga") return "G-4Q2C1NGD40";
  }
  return null;
};

const ActualApp = () => {
  const [gaInitialized, setGAInitialized] = React.useState(false);

  React.useEffect(() => {
    const gaId = getTrackingId("ga");
    if (gaInitialized) {
      return;
    }

    if (gaId) {
      // @ts-ignore
      ReactGA.initialize(gaId);
      setGAInitialized(true);
    }
  }, []);

  return (
    <RouterProvider router={router} context={{ queryClient }}/>
  );
};

const LoadingUserScreen = () => {
  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center">
      <Spinner/>
      Loading user details
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  // <React.StrictMode>
  <PostHogProvider
    apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
    options={{
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      defaults: '2025-05-24',
      capture_exceptions: true,
      debug: import.meta.env.MODE === "development",
    }}
  >
    <InstrumentedErrorBoundaryGenericDisplay>
      <Suspense fallback={<LoadingUserScreen/>}>
        <SidebarProvider>
          <QueryClientProvider client={queryClient}>
            <AllauthProvider
              baseUrl={API_URL}
              csrfTokenEndpoint="/csrf/"
            >
              <InitialLoadContextProvider>
                <DateRangeContextProvider>
                  <ChartOptionsContextProvider>
                    <AllAssetsContextProvider>
                      <SelectedAssetsContextProvider>
                        <ActualApp/>
                        <Toaster/>
                      </SelectedAssetsContextProvider>
                    </AllAssetsContextProvider>
                  </ChartOptionsContextProvider>
                </DateRangeContextProvider>
              </InitialLoadContextProvider>
            </AllauthProvider>
          </QueryClientProvider>
        </SidebarProvider>
      </Suspense>
    </InstrumentedErrorBoundaryGenericDisplay>
  </PostHogProvider>
  // </React.StrictMode>
);
